import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { LanguageSwitch } from "./LanguageSwitch";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Menu, X, LogOut, Sparkles, ArrowUpRight } from "lucide-react";

export const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLink = (to, label, testid, opts = {}) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        className={`relative text-[13px] font-medium transition-colors ${active ? "text-[#0A1128]" : "text-[#0A1128]/65 hover:text-[#0A1128]"}`}
        data-testid={testid}
      >
        <span className="inline-flex items-center gap-1.5">{label}{opts.badge}</span>
        {active && <span className="absolute -bottom-[22px] left-0 right-0 h-[2px] bg-[#F5A623]" />}
      </Link>
    );
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#EAE6DF]"
          : "bg-[#FDFCFB]/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <Logo size="md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLink("/", t("nav.home"), "nav-home")}
          {navLink("/diensten", t("services.view_all"), "nav-diensten")}
          {navLink("/sem", t("nav.sem"), "nav-sem", {
            badge: (
              <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-[#F5A623] text-[#0A1128] uppercase tracking-[0.12em]">
                AI
              </span>
            ),
          })}
          {navLink("/zo-werkt-het", t("nav.how_it_works"), "nav-zo-werkt-het")}
          {navLink("/voor-kantoren", t("nav.for_firms"), "nav-voor-kantoren")}
          {navLink("/over-ons", t("nav.about"), "nav-over-ons")}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitch />
          <span className="w-px h-6 bg-[#EAE6DF]" />
          {user ? (
            <>
              <Link
                to={user.role === "klant" ? "/mijn-matches" : "/dashboard"}
                className="text-[13px] font-semibold text-[#0A1128] hover:text-[#F5A623] transition-colors"
                data-testid="header-dashboard-link"
              >
                {user.role === "klant" ? t("nav.my_matches") : t("nav.dashboard")}
              </Link>
              <button onClick={logout} className="btn-ghost text-[13px] !py-1.5" data-testid="header-logout-btn">
                <LogOut size={14} /> {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-[13px] font-semibold text-[#0A1128] hover:text-[#F5A623] transition-colors"
                data-testid="nav-inloggen"
              >
                {t("nav.login")}
              </Link>
              <button
                onClick={() => navigate("/onboarding")}
                className="btn-amber !py-2 !px-4 text-[13px] group"
                data-testid="header-aanmelden-btn"
              >
                {t("nav.register")}
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitch />
          <button onClick={() => setOpen(!open)} className="p-2 rounded-md hover:bg-[#F3F0EA]" aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#EAE6DF] px-4 py-5 space-y-1 animate-slide-down">
          <Link to="/" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-home">{t("nav.home")}</Link>
          <Link to="/diensten" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-diensten">{t("services.view_all")}</Link>
          <Link to="/sem" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-sem">
            <span className="inline-flex items-center gap-2">
              <Sparkles size={14} className="text-[#F5A623]" /> {t("nav.sem")}
            </span>
            <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-[#F5A623] text-[#0A1128] uppercase tracking-[0.12em]">AI</span>
          </Link>
          <Link to="/zo-werkt-het" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-zo-werkt-het">{t("nav.how_it_works")}</Link>
          <Link to="/voor-klanten" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-voor-klanten">{t("nav.for_clients")}</Link>
          <Link to="/voor-kantoren" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-voor-kantoren">{t("nav.for_firms")}</Link>
          <Link to="/over-ons" onClick={() => setOpen(false)} className="flex items-center justify-between py-3 border-b border-[#EAE6DF]/60 text-[15px] font-medium text-[#0A1128]" data-testid="mobile-nav-over-ons">{t("nav.about")}</Link>
          <div className="pt-3 flex flex-col gap-2">
            {user ? (
              <>
                <Link to={user.role === "klant" ? "/mijn-matches" : "/dashboard"} onClick={() => setOpen(false)} className="btn-navy w-full" data-testid="mobile-dashboard-link">
                  {user.role === "klant" ? t("nav.my_matches") : t("nav.dashboard")}
                </Link>
                <button onClick={() => { setOpen(false); logout(); }} className="btn-outline-navy w-full" data-testid="mobile-logout-btn">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline-navy w-full" data-testid="mobile-inloggen">{t("nav.login")}</Link>
                <Link to="/onboarding" onClick={() => setOpen(false)} className="btn-amber w-full" data-testid="mobile-aanmelden">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
